import { Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../../store';
import { getStoredToken, isTokenExpired, useCurrentUser } from '../../api/auth';
import { accessVerdict, roleResolution } from '../../auth/accessDecisions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: Array<'viewer' | 'analyst' | 'admin'>;
}

export default function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const location = useLocation();
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const userRole = useAppStore((s) => s.user.role);
  const token = getStoredToken();

  // Meme cle de requete qu'AuthProvider : react-query dedoublonne, cet appel
  // n'emet donc aucune requete supplementaire. Il sert a savoir SI le role est
  // en cours de resolution ou si sa resolution a echoue.
  const { isLoading, isFetching, isError, error, refetch } = useCurrentUser();

  // Authentification
  const hasValidToken = token && !isTokenExpired(token);
  if (!isAuthenticated && !hasValidToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const verdict = accessVerdict(userRole, requiredRoles);

  if (verdict === 'granted') {
    return <>{children}</>;
  }

  // ⚠️ D.1308 — « PAS ENCORE SU » N'EST PAS « REFUSE ».
  // Le store ne persiste pas l'identite : au rechargement le role est absent
  // le temps que /auth/me reponde. L'ancien code comparait cette absence aux
  // roles requis et affichait « Acces refuse » a un admin authentifie.
  if (verdict === 'unknownRole') {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    const resolution = roleResolution({
      resolving: isLoading || isFetching,
      failed: isError,
      status,
    });

    // ⚠️ Un 401/403 sur /auth/me EST un refus, pas une panne : depuis le
    // correctif serveur D.1320, c'est la reponse a un jeton qui ne porte
    // aucun role Helios exploitable. Lui dire « ce n'est pas un refus
    // d'acces » serait un mensonge d'ecran.
    if (resolution === 'serverRefused') {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 rounded-xl bg-surface-secondary border border-red-500/30">
            <h2 className="text-xl font-semibold text-white mb-2">Acces refuse</h2>
            <p className="text-text-secondary">
              Votre compte ne porte aucun role Helios exploitable.
            </p>
            <p className="text-text-tertiary text-sm mt-2">
              Role requis : {requiredRoles?.join(', ')}
            </p>
          </div>
        </div>
      );
    }

    if (resolution === 'pending') {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8">
            <div
              className="w-10 h-10 mx-auto mb-4 rounded-full border-2 border-text-tertiary border-t-transparent animate-spin"
              role="status"
              aria-label="Verification des permissions"
            />
            <p className="text-text-secondary">Verification de vos permissions...</p>
          </div>
        </div>
      );
    }

    // La resolution a echoue (ou n'a rien rendu). On le DIT, au lieu de
    // presenter un refus de droits qui accuserait le compte a tort.
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8 rounded-xl bg-surface-secondary border border-amber-500/30">
          <h2 className="text-xl font-semibold text-white mb-2">Role indetermine</h2>
          <p className="text-text-secondary">
            Vos permissions n'ont pas pu etre recuperees. Ce n'est pas un refus d'acces.
          </p>
          {isError && (
            <p className="text-text-tertiary text-sm mt-2">
              La verification aupres du serveur a echoue.
            </p>
          )}
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 rounded-lg bg-surface-tertiary text-white hover:bg-surface-primary transition-colors"
          >
            Reessayer
          </button>
        </div>
      </div>
    );
  }

  // verdict === 'denied' : le role est CONNU et ne suffit pas.
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center p-8 rounded-xl bg-surface-secondary border border-red-500/30">
        <div className="text-4xl mb-4 text-red-400">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-white mb-2">Acces refuse</h2>
        <p className="text-text-secondary">
          Vous n'avez pas les permissions necessaires pour acceder a cette page.
        </p>
        <p className="text-text-tertiary text-sm mt-2">
          Role requis : {requiredRoles?.join(', ')} — Votre role : {userRole}
        </p>
      </div>
    </div>
  );
}
